import React from 'react';
import proptypes from 'prop-types';
import classNames from 'classnames';
import {
  Button, Typography, Pill, useBreakpoint
} from '@one-thd/sui-atomic-components';
import { Filters, ArrowDown } from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import { PrimaryFiltersDrawer } from './PrimaryFiltersDrawer';

const {
  func, string, arrayOf, shape, bool
} = proptypes;

const PrimaryFilters = ({
  dimensions,
  appliedDimensions,
  onRefinementChange,
  onCloseDrawer,
  baseUrl,
  canonicalUrl,
  primaryFilters,
  hideViewAll,
  isOpen,
  activeDimension,
  onSecondaryFilterDrawerToggle,
  responseAppliedDimensions
}) => {
  const { lesser } = useBreakpoint('md', { ssr: true });
  const isPlpSpeedEnabled = useConfigService('fs-prop:isPlpSpeedEnabled');

  if (!dimensions) {
    return null;
  }
  if (dimensions.length === 0) {
    return null;
  }
  const renderPrimaryFiltersButton = (dimension) => {
    const commonProps = {
      key: dimension.dimensionId,
      'data-testid': 'primary-filters-btn',
      'aria-label': dimension.label
    };

    const selectedDimensionsLength = responseAppliedDimensions.find((ad) => ad.label === dimension.label)?.refinements?.length || '';

    return isPlpSpeedEnabled ? (
      <Pill
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...commonProps}
        label={dimension.label + (selectedDimensionsLength ? ` (${selectedDimensionsLength})` : '')}
        size={lesser ? 'regular' : 'large'}
        arrow={{
          direction: 'down',
          position: 'trailingSlot'
        }}
        selected={!!selectedDimensionsLength}
        variant="outline"
        onClick={() => onCloseDrawer(true, dimension)}
      />
    ) : (
      <Button
        variant="ghost"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...commonProps}
        onClick={() => onCloseDrawer(true, dimension)}
      >
        <div className="sui-pr-2 sui-text-nowrap">
          <Typography variant="body-base" color="sui-bg-primary">{dimension.label}</Typography>
        </div>
        <ArrowDown size="small" />
      </Button>
    );
  };

  const renderFilterButtons = (primaryFiltersDimensions) => {
    if (primaryFiltersDimensions.length === 0) {
      return null;
    }
    return (
      <div className={classNames('sui-flex', lesser ? 'sui-gap-3' : 'sui-gap-4')}>
        {!hideViewAll && (
          isPlpSpeedEnabled ? (
            <Pill
              data-testid="all-filters-button"
              leadingSlot={<Filters />}
              label={<span className="sui-font-bold">All Filters</span>}
              size={lesser ? 'regular' : 'large'}
              arrow={{
                direction: 'down',
                position: 'trailingSlot'
              }}
              variant="outline"
              onClick={() => onSecondaryFilterDrawerToggle(true)}
            />
          ) : (
            <Button
              data-testid="all-filters-button"
              variant="ghost"
              onClick={() => onSecondaryFilterDrawerToggle(true)}
            >
              <Filters />
              <div className="sui-pl-2 sui-text-nowrap">
                <Typography weight="bold" color="none">All Filters</Typography>
              </div>
            </Button>
          )
        )}
        {
          primaryFiltersDimensions.map((dimension) => (
            renderPrimaryFiltersButton(dimension)
          ))
        }
      </div>
    );
  };

  return (
    <div data-component="PrimaryFilterDimensions">

      {renderFilterButtons(primaryFilters)}

      <PrimaryFiltersDrawer
        isOpen={isOpen}
        activeDimension={activeDimension}
        onCloseDrawer={onCloseDrawer}
        dimensions={dimensions}
        appliedDimensions={appliedDimensions}
        onDimensionChange={onRefinementChange}
        canonicalUrl={canonicalUrl}
        baseUrl={baseUrl}
      />
    </div>
  );
};

PrimaryFilters.propTypes = {
  baseUrl: string.isRequired,
  canonicalUrl: string.isRequired,
  dimensions: arrayOf(
    shape({
      label: string.isRequired
    })
  ).isRequired,
  primaryFilters: arrayOf(
    shape({
      label: string.isRequired
    })
  ).isRequired,
  appliedDimensions: arrayOf(shape({})).isRequired,
  activeDimension: shape({
    dimensionId: string,
    label: string,
    refinements: arrayOf(shape({}))
  }).isRequired,
  onRefinementChange: func.isRequired,
  onCloseDrawer: func.isRequired,
  onSecondaryFilterDrawerToggle: func.isRequired,
  hideViewAll: bool,
  isOpen: bool.isRequired,
  responseAppliedDimensions: arrayOf(shape({}))
};

PrimaryFilters.defaultProps = {
  hideViewAll: false,
  responseAppliedDimensions: []
};

PrimaryFilters.displayName = 'PrimaryFilters';

export { PrimaryFilters };
